/**
 * LEADS-OBJECT-CRM-SHELL-01 — pure helpers for the owner Leads CRM object surface.
 *
 * Shared, PHI-free, DETERMINISTIC projection helpers used by BOTH the compact
 * Leads list (`/dashboard/owner/leads`) and the Lead record detail
 * (`/dashboard/owner/leads/:safeLeadId`). Pure by construction — no React, no
 * fetch, no Supabase, no `Date.now()` / `Math.random()`, no mutation — so it
 * unit-tests without jsdom and stays decoupled from the presentation layer
 * (mirrors the `lead-360-view.ts` doctrine).
 *
 * Honesty rules carried straight through from `lead-360-view.ts`:
 *   - Activity (`events[]`) is "what happened to the lead"; it is DISTINCT from
 *     telephony / walk-in / revenue LINKAGE. `latestActivitySummary` only ever
 *     describes the activity stream — it never reads a linkage gap as activity,
 *     and never says "not called".
 *   - A wired-but-empty stream (`eventStreamStatus: 'available'` + no events)
 *     reads as "connected — no activity recorded yet", NEVER as the legacy
 *     `'not_wired'` state and NEVER as "no activity happened".
 */

import type { Lead360View } from '@/dashboard/owner/lead-360-view';

/** CRM-style safe reference, e.g. "Lead #36". `safeLeadId` is opaque (not PHI). */
export function formatLeadRef(safeLeadId: string): string {
  return `Lead #${safeLeadId}`;
}

/**
 * Owner Lead-detail route for a safe lead reference. `clinicId` is carried as a
 * query param so the detail page can scope its single-clinic read without an
 * extra clinic-list round-trip (the backend lead-360 read is per-clinic). A null
 * / empty clinic omits the param; the detail page then falls back to the first
 * clinic in scope.
 */
export function leadDetailHref(
  safeLeadId: string,
  clinicId: number | string | null,
): string {
  const base = `/dashboard/owner/leads/${encodeURIComponent(safeLeadId)}`;
  if (clinicId === null || String(clinicId).trim() === '') return base;
  return `${base}?clinicId=${encodeURIComponent(String(clinicId))}`;
}

/** Honest wired-but-empty activity copy (stream connected, zero events yet). */
export const ACTIVITY_EMPTY_WIRED =
  'Activity stream connected — no activity recorded yet.';

/** Honest legacy/omitted-status empty copy (no reader produced this stream). */
export const ACTIVITY_NOT_WIRED = 'Activity stream not wired yet.';

/**
 * SHORT, table-friendly empty-activity copy for the compact Leads LIST
 * (VISUAL-POLISH-01). The full sentences above are honest but too repetitive once
 * per row, so list rows use these terser forms. They stay DISTINCT (wired-empty is
 * NOT not-wired) and neither reads as "not called": a wired-but-empty stream is
 * "No activity yet" (connected, nothing recorded yet); a never-wired stream is
 * "Not wired yet". The detail record keeps the full sentences.
 */
export const ACTIVITY_EMPTY_WIRED_SHORT = 'No activity yet';
export const ACTIVITY_NOT_WIRED_SHORT = 'Not wired yet';

/**
 * One-line "latest activity" summary for a lead, derived ONLY from the activity
 * stream (`events[]`) — never from linkage status. With events present, returns
 * the sanitized summary of the MOST RECENT event (greatest `occurredAt`; ISO-8601
 * UTC sorts lexicographically, ties resolve to the later array element). With no
 * events, returns the HONEST wired-empty vs not-wired copy — never "not called",
 * never "no activity happened". Pure: reads the already-projected `Lead360View`
 * so the `eventStreamStatus` precedence stays single-sourced in `buildLead360View`.
 */
function latestEventSummaryOrNull(view: Lead360View): string | null {
  const events = view.events;
  if (events.length === 0) return null;
  let latest = events[0];
  for (const ev of events) {
    if (ev.occurredAt >= latest.occurredAt) latest = ev;
  }
  return latest.summary;
}

export function latestActivitySummary(view: Lead360View): string {
  const summary = latestEventSummaryOrNull(view);
  if (summary !== null) return summary;
  return view.eventStreamStatus === 'available'
    ? ACTIVITY_EMPTY_WIRED
    : ACTIVITY_NOT_WIRED;
}

/**
 * Like `latestActivitySummary`, but with SHORT, table-friendly empty-state copy
 * for the compact Leads list (VISUAL-POLISH-01). An event summary is returned
 * verbatim (already short, e.g. "Called via phone"); only the empty states differ.
 * Keeps wired-empty DISTINCT from not-wired and never reads as "not called".
 */
export function latestActivitySummaryShort(view: Lead360View): string {
  const summary = latestEventSummaryOrNull(view);
  if (summary !== null) return summary;
  return view.eventStreamStatus === 'available'
    ? ACTIVITY_EMPTY_WIRED_SHORT
    : ACTIVITY_NOT_WIRED_SHORT;
}

/**
 * The ISO instant of the MOST RECENT activity event (greatest `occurredAt`;
 * ISO-8601 UTC sorts lexicographically, ties resolve to the later element),
 * or null when the stream carries no events. Companion to
 * `latestActivitySummaryShort` for the compact list activity cell
 * (LEADS-LIST-AEDS-ENTERPRISE-IMPLEMENTATION-V1 M5): the summary is the chip
 * text, this is the chip's compact date. A pure read of the already-projected
 * view -- never fabricates a timestamp when there is no event. Pure.
 */
export function latestActivityInstant(view: Lead360View): string | null {
  const events = view.events;
  if (events.length === 0) return null;
  let latest = events[0];
  for (const ev of events) {
    if (ev.occurredAt >= latest.occurredAt) latest = ev;
  }
  return latest.occurredAt;
}

/**
 * Humanized lifecycle-stage label for the lead record header / highlights. Maps
 * the view-model's `lifecycleCompleteness` to operator-facing wording without
 * inventing certainty (`unknown` stays "Unknown").
 */
export function lifecycleStageLabel(view: Lead360View): string {
  switch (view.lifecycleCompleteness) {
    case 'lead_only':
      return 'Lead captured';
    case 'partial':
      return 'Source attributed';
    case 'lead_plus_walkin':
      return 'Walk-in linked';
    case 'complete':
      return 'Fully linked';
    case 'unknown':
      return 'Unknown';
    default: {
      const _exhaustive: never = view.lifecycleCompleteness;
      void _exhaustive;
      return 'Unknown';
    }
  }
}

/**
 * SINGLE page-level data note for the Leads list — replaces the long per-record
 * read-model caveat that used to repeat once per lead row. Low-emphasis, honest,
 * and generic; per-lead provenance / caveat stays on the lead detail record.
 */
export const LEADS_DATA_NOTE =
  'PHI-free, read-only lead records. Activity is shown separately from ' +
  'integration linkage — Office24x7, walk-in, revenue — so “not linked yet” ' +
  'never means “not contacted”. Gaps are shown honestly, never inferred.';

/**
 * Accurate-security replacement for the stale "PHI-free" wording above
 * (LEADS-WORKSPACE-ROLE-SCOPED-01 inherited item): the list shows lead
 * names to scoped staff (CONTACT-REVEAL-AUDIT-01 carve-out) and the
 * record hosts the audited phone reveal, so "PHI-free" no longer
 * describes the surface. Rendered ONLY when the caller opts in
 * (flag-gated via me.leadsWorkspaceEnabled / the workspace pages); the
 * V1 note above stays byte-identical for the flag-off render.
 */
export const LEADS_DATA_NOTE_V2 =
  'Read-only lead records scoped to your verified clinic access. Lead ' +
  'names are visible to scoped staff; phone numbers are never shown on ' +
  'this list and live behind the audited reveal on the lead record. ' +
  'Activity is shown separately from integration linkage — Office24x7, ' +
  'walk-in, revenue — so “not linked yet” never means “not contacted”. ' +
  'Gaps are shown honestly, never inferred.';
